import React, { useMemo } from 'react';
import { useInterpret } from '@xstate/react';
import type { CheckoutMachineInterpreterObserver } from './machines/externalCheckout';
import { createCheckoutMachine } from './machines/externalCheckout';
import { Provider } from './hooks/useCheckoutFunnel';
import type { CheckoutDialogFields, CheckoutFunnelProviderProps } from './CheckoutDialog.types';
import useCheckoutDialogController from './CheckoutDialog.controller';
import { useFeatureValue } from '@shared/core/featureFlags';
import { parseGiftWrapPayload } from '@shared/components/GiftWrap';
import { useRegistryEventTaxDestination } from '@apps/registry/common/hooks/useRegistryEventTaxDestination';
import { pxToRem } from '@withjoy/joykit/theme';
import { SkeletonText } from '@shared/components/Skeleton';
import { LayoutShell } from './components';
import { SkeletonImage, styles as layoutShellStyles } from '@apps/registry/guest/routes/GuestRegistry/components/PurchaseDialog/components/LayoutShell/LayoutShell.styles';

/**
 * Mirrors the `Setup` route's skeleton, minus its `TopNav` — that depends on funnel context, which
 * does not exist yet at this point. The dialog chrome (and its close button) lives outside this
 * provider in `CheckoutDialog`, so the frame is already on screen while this renders.
 */
const CheckoutFunnelSkeleton = () => (
  <LayoutShell>
    <LayoutShell.Body paddingTop={6}>
      <LayoutShell.MediaPanel>
        <SkeletonImage
          __css={layoutShellStyles.skeletonImage}
          margin="0 auto"
        />
      </LayoutShell.MediaPanel>
      <LayoutShell.ContentPanel>
        <SkeletonText
          marginBottom={5}
          width="100%"
          skeletonHeight={pxToRem(31)}
          rows={1}
        />
        <SkeletonText
          marginBottom={6}
          width="100%"
          skeletonHeight={pxToRem(48)}
          rows={3}
        />
        <SkeletonText
          marginBottom={7}
          width="100%"
          skeletonHeight={pxToRem(100)}
          rows={1}
        />
        <SkeletonText
          marginBottom={7}
          width="100%"
          skeletonHeight={pxToRem(48)}
          rows={1}
        />
      </LayoutShell.ContentPanel>
    </LayoutShell.Body>
  </LayoutShell>
);

/**
 * TODO:
 *  - handle authenticated email session (should use useStoredGiftReservationFieldsContext)
 *  - handle calling `updateDataProvider`
 */
export function CheckoutFunnelProvider(props: CheckoutFunnelProviderProps): React.ReactElement {
  const { registryEventTaxDestination, loading: isTaxDestinationLoading } = useRegistryEventTaxDestination(props.eventId);
  const isDestinationTaxable = registryEventTaxDestination != null;

  if (isTaxDestinationLoading) {
    // Matches the `Setup` route: only the wide affiliate/group-gifting layouts get a skeleton,
    // the compact funnel renders nothing rather than a visibly empty panel.
    return props.isAffiliate || props.isGroupGifting ? <CheckoutFunnelSkeleton /> : <></>;
  }

  return (
    <CheckoutFunnelProviderInternal
      {...props}
      isDestinationTaxable={isDestinationTaxable}
    />
  );
}

const CheckoutFunnelProviderInternal = ({
  children,
  onClose,
  registryItemId,
  currentOrderId,
  registryState,
  onPurchaseGift,
  isAdmin,
  isAffiliate,
  isGiftWrapPurchased,
  isGroupGifting,
  donationAmount,
  eventId,
  eventHandle,
  isDestinationTaxable,
}: CheckoutFunnelProviderProps & Readonly<{ isDestinationTaxable: boolean }>): React.ReactElement => {
  const { value: registryGiftWrapCashOn } = useFeatureValue('registryGiftWrapCashEnabled');
  const { value: registryGiftWrapAffiliateOn } = useFeatureValue('registryGiftWrapAffiliateEnabled');
  const {
    handleAddToCart,
    handleCompletePurchase,
    handleRemoveFromCart,
    handleAnnotateRegistryOrder,
    handleCreatePurchaseContext,
    currentOrderIdState,
    handleCreditCardPurchase,
    creditCardError,
  } = useCheckoutDialogController({
    registryState,
    registryItemId,
    currentOrderId,
    isAdmin,
    isAffiliate,
    isGroupGifting,
    eventId,
    eventHandle,
  });

  const observer = useMemo<CheckoutMachineInterpreterObserver>(() => {
    return {
      next: () => {
        // console.log('>>> next', { state });
      },
      error: () => {
        // console.log('>>> error', error);
      },
      complete: () => {
        // console.log('>>> complete');
      },
    };
  }, []);

  const getMachine = () =>
    createCheckoutMachine(
      isAffiliate,
      isGroupGifting,
      donationAmount,
      registryGiftWrapCashOn === 'on',
      registryGiftWrapAffiliateOn === 'on',
      isGiftWrapPurchased,
      isDestinationTaxable,
    );

  const service = useInterpret(
    getMachine,
    {
      actions: {
        onCloseDialog: () => {
          onClose();
        },

        reserveAndAddToCart: async ({ formValues }, event) => {
          switch (event.type) {
            case 'SUBMIT_GUEST_DETAILS': {
              const { name, email } = formValues;
              if (name && email && isAffiliate) {
                await handleAddToCart(formValues as CheckoutDialogFields);
              }
              return;
            }
            case 'SELECT_PAYMENT_METHOD': {
              const { name, email, paymentMethod } = formValues;
              if (name && email && paymentMethod) {
                await handleAddToCart(formValues as CheckoutDialogFields);
              }
              return;
            }
            case 'SUBMIT_GIFT_NOTE': {
              const { name, email, paymentMethod } = formValues;
              if (name && email && (paymentMethod || isAffiliate)) {
                await handleAddToCart(formValues as CheckoutDialogFields);
              }
              return;
            }
            case 'SUBMIT_GIFT_WRAP': {
              const { name, email, paymentMethod, note } = formValues;
              const payload = parseGiftWrapPayload(note || '');

              if (name && email && (paymentMethod || isAffiliate)) {
                await handleAddToCart({ ...(formValues as CheckoutDialogFields), note: payload?.message || note || '' });
              }
              return;
            }
          }
        },

        removeFromCart: async (_context, event) => {
          switch (event.type) {
            case 'CONFIRM_CANCEL': {
              handleRemoveFromCart();
              return;
            }
          }
        },

        markGiftAsPurchased: async ({ formValues }) => {
          handleCompletePurchase(formValues as CheckoutDialogFields, onPurchaseGift);
          onPurchaseGift?.();
        },
        annotateRegistryOrder: async ({ formValues }) => {
          handleAnnotateRegistryOrder(formValues as CheckoutDialogFields);
        },
        createPurchaseContext: async state => {
          handleCreatePurchaseContext(
            state.formValues as CheckoutDialogFields,
            state.registryItemId!,
            state.isAffiliate,
            state.registryItemName || '',
            (state.registryItemPrice ? state.registryItemPrice / 100 : state.formValues.amount) || 0,
          );
        },
        createCreditCardPurchase: async state => {
          handleCreditCardPurchase(state.formValues as CheckoutDialogFields, state.registryItemId!);
        },
      },
    },
    observer,
  );

  const ctx = useMemo(() => {
    return {
      service,
      currentOrderIdState,
      creditCardError,
    };
  }, [service, currentOrderIdState, creditCardError]);

  return <Provider value={ctx}>{children}</Provider>;
};
